/**
 * 皮肤与深浅的状态。**纯逻辑，不含 React** —— 这样能用 node:test 直接测，
 * 不用为几十行状态拖进一整套 DOM 测试框架。
 */

export const SKINS = ['laroluna', 'midnight', 'sunset'] as const;
export type Skin = (typeof SKINS)[number];

/** `system` = 跟系统走，不写 data-theme */
export const MODES = ['system', 'dark', 'light'] as const;
export type Mode = (typeof MODES)[number];

export interface ThemeState {
  skin: Skin;
  mode: Mode;
}

export const DEFAULT_THEME: ThemeState = { skin: 'laroluna', mode: 'dark' };

const KEY = 'lobby.theme';

export function isSkin(v: unknown): v is Skin {
  return typeof v === 'string' && (SKINS as readonly string[]).includes(v);
}
export function isMode(v: unknown): v is Mode {
  return typeof v === 'string' && (MODES as readonly string[]).includes(v);
}

/**
 * 从存下来的字符串还原。**任何看不懂的输入都退回默认值，不抛。**
 * 用户的 localStorage 里可能是上个版本的皮肤名、也可能是被别的脚本写坏的 ——
 * 那不该让整个大厅白屏。
 */
export function parseTheme(raw: string | null): ThemeState {
  if (!raw) return DEFAULT_THEME;
  try {
    const o = JSON.parse(raw) as Partial<ThemeState>;
    return {
      skin: isSkin(o.skin) ? o.skin : DEFAULT_THEME.skin,
      mode: isMode(o.mode) ? o.mode : DEFAULT_THEME.mode,
    };
  } catch {
    return DEFAULT_THEME;
  }
}

export function loadTheme(store: Pick<Storage, 'getItem'> | null): ThemeState {
  try {
    return parseTheme(store?.getItem(KEY) ?? null);
  } catch {
    // 隐私模式下读 localStorage 会抛。用默认值继续，别让它挡住启动。
    return DEFAULT_THEME;
  }
}

export function saveTheme(store: Pick<Storage, 'setItem'> | null, t: ThemeState): void {
  try {
    store?.setItem(KEY, JSON.stringify(t));
  } catch { /* 存不下就算了，本次会话内仍然生效 */ }
}

/**
 * 把状态写到 <html> 上。**换肤就是这两个属性变一下** ——
 * 不重挂组件、不重新加载 CSS、没有闪白。
 */
export function applyTheme(root: HTMLElement, t: ThemeState): void {
  root.dataset.skin = t.skin;
  if (t.mode === 'system') delete root.dataset.theme;
  else root.dataset.theme = t.mode;
}

/** 浏览器地址栏/状态栏的颜色。要跟着皮肤走，否则深色皮肤配白色状态栏很难看。 */
export function syncThemeColor(doc: Document): void {
  const meta = doc.querySelector('meta[name="theme-color"]');
  if (!meta) return;
  const bg = getComputedStyle(doc.documentElement).getPropertyValue('--c-bg').trim();
  if (bg) meta.setAttribute('content', bg);
}
